import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  AlarmClock,
  AlertTriangle,
  Bell,
  CalendarCheck,
  CalendarClock,
  CalendarRange,
  CheckCircle2,
  ChevronRight,
  CreditCard,
  FileWarning,
  Flame,
  Inbox,
  Loader2,
  PiggyBank,
  Plus,
  RefreshCw,
  Tag,
  Wallet,
} from 'lucide-react';
import { Button } from '@components/Button';
import { PermissionGuard } from '@components/PermissionGuard';
import { ActivityRow } from '@components/activity/ActivityBits';
import { useAuth } from '@contexts/AuthContext';
import { useProfile } from '@contexts/ProfileContext';
import { useActivityModal } from '@contexts/ActivityModalContext';
import { usePermissions } from '@hooks/usePermissions';
import { ExpiringRow } from '@pages/cac/ExpiringView';
import { AmountText, BalanceText, ColorChip, InvoiceStatusBadge } from '@pages/finance/financeBits';
import dashboardService from '@services/dashboardService';
import transactionService from '@services/transactionService';
import type { Activity } from '@/types/activity';
import type { DashboardSummary, FinanceSummary, ScheduleItem, SchedulingSummary } from '@/types/planning';
import { BOARD_TYPE_LABELS } from '@/types/board';
import { BUDGET_STATUS_LABELS, type BudgetItem, type BudgetSummary, type CreditCardInvoice } from '@/types/finance';
import { colorClasses } from '@/utils/colors';
import { diffDaysISO, formatDateBR } from '@/utils/formatters';
import { formatBRL } from '@/utils/money';
import { monthLabel } from '@/utils/period';
import { budgetStatusColor, budgetStatusOf, clampPct, formatPct } from '@/utils/reportUtils';

/** Linha do widget "Próximas faturas": cartão · rótulo, vencimento, total e situação; abre o cartão. */
const InvoiceRow: React.FC<{ invoice: CreditCardInvoice; today: string }> = ({ invoice, today }) => {
  const days = diffDaysISO(today, invoice.due_date);
  const when = days < 0 ? `vencida há ${-days} ${-days === 1 ? 'dia' : 'dias'}` : days === 0 ? 'vence hoje' : `vence em ${days} ${days === 1 ? 'dia' : 'dias'}`;
  return (
    <Link to={`/finance/cards?open=${invoice.credit_card_id}&invoice=${invoice.id}`} className="flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-slate-50 transition-colors">
      <span className={clsx('p-1.5 rounded-lg flex-shrink-0', colorClasses(invoice.credit_card_color).soft, colorClasses(invoice.credit_card_color).text)}>
        <CreditCard size={16} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-slate-800 truncate">
          {invoice.credit_card_name} · {invoice.label}
        </p>
        <p className="text-small truncate">
          {formatDateBR(invoice.due_date)} · {when}
        </p>
      </div>
      <AmountText cents={invoice.total_cents} className="text-sm font-medium" />
      <InvoiceStatusBadge status={invoice.status} className="hidden sm:inline-flex" />
      <ChevronRight size={16} className="text-slate-300 flex-shrink-0" />
    </Link>
  );
};

/** Mês corrente a partir de `today` (AAAA-MM-DD) — sem `new Date`, como manda a regra de datas. */
const monthOfToday = (today: string): { year: number; month: number } => {
  const m = /^(\d{4})-(\d{2})/.exec(today);
  return m ? { year: Number(m[1]), month: Number(m[2]) } : { year: 0, month: 1 };
};

/**
 * Widget "Finanças" (Fase 6): saldo de hoje das contas ativas (grande), previsto até o fim do mês,
 * receitas/despesas/resultado realizados no mês, as contas com cor e saldo e o aviso de lançamentos
 * sem categoria (link para Lançamentos filtrado). Só aparece quando o backend manda `finance`.
 */
const FinanceWidget: React.FC<{ finance: FinanceSummary; today: string }> = ({ finance, today }) => {
  const ym = monthOfToday(today);
  const projectedDiff = finance.projected_balance_cents - finance.accounts_balance_cents;
  return (
    <div className="card p-4">
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 mb-3">
        <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
          <Wallet size={16} className="text-emerald-600" />
          Finanças
        </h3>
        <Link to="/finance/accounts" className="text-xs text-emerald-700 hover:underline">
          Ver contas
        </Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Saldo de hoje</p>
          <p className="text-3xl font-semibold leading-tight">
            <BalanceText cents={finance.accounts_balance_cents} />
          </p>
          <p className="text-small mt-1 flex flex-wrap items-center gap-x-1">
            <span>previsto até o fim do mês</span>
            <BalanceText cents={finance.projected_balance_cents} className="italic font-medium" />
            {projectedDiff !== 0 && (
              <span className="whitespace-nowrap">
                ({projectedDiff > 0 ? '+' : ''}
                {formatBRL(projectedDiff)} a vencer)
              </span>
            )}
          </p>
        </div>
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold first-letter:uppercase">{monthLabel(ym)} · realizado</p>
          <dl className="mt-1 space-y-0.5 text-sm">
            <div className="flex items-center justify-between gap-2">
              <dt className="text-slate-600">Receitas</dt>
              <dd className="tabular-nums text-emerald-700 whitespace-nowrap">{formatBRL(Math.abs(finance.month_income_cents))}</dd>
            </div>
            <div className="flex items-center justify-between gap-2">
              <dt className="text-slate-600">Despesas</dt>
              <dd className="tabular-nums text-red-600 whitespace-nowrap">{formatBRL(-Math.abs(finance.month_expense_cents))}</dd>
            </div>
            <div className="flex items-center justify-between gap-2 border-t border-slate-100 pt-0.5">
              <dt className="text-slate-800 font-medium">Resultado</dt>
              <dd className="font-semibold">
                <BalanceText cents={finance.month_net_cents} />
              </dd>
            </div>
          </dl>
        </div>
        <div className="min-w-0 sm:col-span-2 lg:col-span-1">
          <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Contas</p>
          {finance.accounts.length === 0 ? (
            <p className="text-sm text-slate-400 mt-1">Nenhuma conta ativa.</p>
          ) : (
            <ul className="mt-1 space-y-0.5 text-sm">
              {finance.accounts.map((a) => (
                <li key={a.id}>
                  <Link to={`/finance/accounts?open=${a.id}`} className="flex items-center justify-between gap-2 rounded px-1 -mx-1 hover:bg-slate-50">
                    <ColorChip color={a.color} className="text-slate-700 min-w-0">
                      {a.name}
                    </ColorChip>
                    <BalanceText cents={a.balance_cents} className="text-sm" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {finance.uncategorized_count > 0 && (
        <Link
          to="/finance/transactions?uncategorized=1"
          className="mt-3 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 hover:bg-amber-100 transition-colors"
        >
          <Tag size={15} className="flex-shrink-0" />
          <span className="min-w-0 [overflow-wrap:anywhere]">
            <strong>{finance.uncategorized_count}</strong> {finance.uncategorized_count === 1 ? 'lançamento sem categoria' : 'lançamentos sem categoria'} neste mês — classificar
          </span>
          <ChevronRight size={16} className="ml-auto flex-shrink-0 text-amber-400" />
        </Link>
      )}
    </div>
  );
};

/** "vence hoje" / "vence em N dias" / "venceu há N dias" a partir de `days_until_due`. */
const dueText = (days: number): string =>
  days < 0 ? `venceu há ${-days} ${-days === 1 ? 'dia' : 'dias'}` : days === 0 ? 'vence hoje' : `vence em ${days} ${days === 1 ? 'dia' : 'dias'}`;

/** Lançamentos já filtrados nas contas a agendar (mesmo link do e-mail e do sino). */
const SCHEDULE_PENDING_LINK = '/finance/transactions?agendamento=pendente';

/** Linha do widget "Contas a agendar": vencida sem agendar (vermelha) ou na janela de aviso (âmbar). */
const ScheduleRow: React.FC<{ item: ScheduleItem; missed: boolean; canWrite: boolean; busy: boolean; onSchedule: (i: ScheduleItem) => void }> = ({
  item,
  missed,
  canWrite,
  busy,
  onSchedule,
}) => (
  <li className="flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-1 px-2 py-2">
    <span className={clsx('p-1.5 rounded-lg flex-shrink-0', missed ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600')}>
      {missed ? <AlertTriangle size={16} /> : <CalendarClock size={16} />}
    </span>
    <div className="min-w-0 flex-1">
      <p className="text-sm font-medium text-slate-800 [overflow-wrap:anywhere]">{item.description}</p>
      <p className="text-small">
        <span className="truncate">{item.account_name}</span> · {formatDateBR(item.transaction_date)} ·{' '}
        <span className={clsx('whitespace-nowrap font-medium', missed ? 'text-red-600' : 'text-amber-700')}>{dueText(item.days_until_due)}</span>
      </p>
    </div>
    <AmountText cents={item.amount_cents} className="text-sm font-medium ml-auto sm:ml-0" />
    {canWrite && (
      <Button variant="outline" size="sm" icon={<CalendarCheck />} loading={busy} onClick={() => onSchedule(item)} className="w-full sm:w-auto">
        Agendada
      </Button>
    )}
  </li>
);

/**
 * Widget "Contas a agendar" (Fase 7): despesas pagas por agendamento no banco — vencidas sem agendar
 * (vermelho) e na janela de aviso (âmbar) — com o botão "Agendada" (POST /transactions/:id/schedule e
 * recarrega o painel) e o rodapé com as já agendadas nos próximos 30 dias. Só com `scheduling`.
 */
const SchedulingWidget: React.FC<{ scheduling: SchedulingSummary; onChanged: () => void }> = ({ scheduling, onChanged }) => {
  const { checkPermission } = usePermissions();
  const canWrite = checkPermission('financas.write').hasPermission;
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pending = scheduling.missed.length + scheduling.to_schedule.length;

  const markScheduled = async (item: ScheduleItem) => {
    setBusyId(item.transaction_id);
    setError(null);
    try {
      await transactionService.schedule(item.transaction_id, true);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao marcar como agendada');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="card p-4">
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 mb-2">
        <h3 className="text-base font-semibold text-slate-800 flex flex-wrap items-center gap-2">
          <CalendarClock size={16} className="text-amber-600" />
          Contas a agendar
          <span className={pending > 0 ? 'badge-warning' : 'badge-neutral'}>{pending}</span>
          {scheduling.missed.length > 0 && (
            <span className="badge-danger" title="Venceram sem ser marcadas como agendadas">
              {scheduling.missed.length} {scheduling.missed.length === 1 ? 'venceu' : 'venceram'}
            </span>
          )}
        </h3>
        <Link to={SCHEDULE_PENDING_LINK} className="text-xs text-emerald-700 hover:underline">
          Ver em Lançamentos
        </Link>
      </div>
      {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-1.5 mb-2">{error}</p>}
      {pending === 0 ? (
        <p className="text-sm text-slate-400 py-4 text-center">Nenhuma conta para agendar agora.</p>
      ) : (
        <ul className="divide-y divide-slate-100 -mx-1">
          {scheduling.missed.map((i) => (
            <ScheduleRow key={i.transaction_id} item={i} missed canWrite={canWrite} busy={busyId === i.transaction_id} onSchedule={markScheduled} />
          ))}
          {scheduling.to_schedule.map((i) => (
            <ScheduleRow key={i.transaction_id} item={i} missed={false} canWrite={canWrite} busy={busyId === i.transaction_id} onSchedule={markScheduled} />
          ))}
        </ul>
      )}
      <div className="mt-2 pt-2 border-t border-slate-100 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-small">
        <span className="inline-flex items-center gap-1">
          <CheckCircle2 size={13} className="text-emerald-600" />
          {scheduling.scheduled_next_30_count} {scheduling.scheduled_next_30_count === 1 ? 'agendada' : 'agendadas'} nos próximos 30 dias ({formatBRL(scheduling.scheduled_next_30_cents)})
          {scheduling.upcoming_next_30_count > 0 && <span> · {scheduling.upcoming_next_30_count} aguardando a janela de aviso</span>}
        </span>
        <Link to={SCHEDULE_PENDING_LINK} className="text-emerald-700 hover:underline whitespace-nowrap">
          Ver pendentes
        </Link>
      </div>
    </div>
  );
};

/** Barra do orçamento (geral ou por categoria) colorida pela situação, com o %. */
const BudgetMiniBar: React.FC<{ pct: number; status: BudgetItem['status'] }> = ({ pct, status }) => {
  const tone = budgetStatusColor(status);
  return (
    <div className="flex items-center gap-2" title={BUDGET_STATUS_LABELS[status]}>
      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(clampPct(pct))}>
        <div className={clsx('h-full rounded-full', tone.bar)} style={{ width: `${clampPct(pct)}%` }} />
      </div>
      <span className={clsx('text-xs font-medium w-12 text-right tabular-nums', tone.text)}>{formatPct(pct)}</span>
    </div>
  );
};

/** Widget "Orçamento de <mês>" (Fase 5): barra geral + até 5 categorias orçadas de maior uso. */
const BudgetWidget: React.FC<{ budget: BudgetSummary }> = ({ budget }) => {
  const overallPct = budget.total_budget_cents > 0 ? (budget.total_spent_cents / budget.total_budget_cents) * 100 : 0;
  const overallStatus = budgetStatusOf(budget.total_spent_cents, budget.total_budget_cents);
  const exceeded = budget.items.filter((i) => i.status === 'exceeded').length;
  return (
    <div className="card p-4">
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 mb-2">
        <h3 className="text-base font-semibold text-slate-800 flex flex-wrap items-center gap-2">
          <PiggyBank size={16} className="text-emerald-600" />
          <span className="first-letter:uppercase">Orçamento de {monthLabel({ year: budget.year, month: budget.month })}</span>
          {exceeded > 0 && (
            <span className="badge-danger" title="Categorias com o orçamento estourado">
              {exceeded} {exceeded === 1 ? 'estourada' : 'estouradas'}
            </span>
          )}
        </h3>
        <Link to={`/finance/budget?year=${budget.year}&month=${budget.month}`} className="text-xs text-emerald-700 hover:underline">
          Ver orçamento
        </Link>
      </div>
      <p className="text-small mb-1">
        <span className="text-slate-800 font-medium tabular-nums">{formatBRL(budget.total_spent_cents)}</span> de {formatBRL(budget.total_budget_cents)} · realizado até hoje
      </p>
      <BudgetMiniBar pct={overallPct} status={overallStatus} />
      {budget.items.length > 0 && (
        <ul className="mt-3 space-y-2">
          {budget.items.slice(0, 5).map((i) => (
            <li key={i.category_id} className="grid grid-cols-1 sm:grid-cols-[minmax(0,11rem)_1fr] sm:items-center gap-x-3 gap-y-0.5">
              <ColorChip color={i.color} className="text-sm text-slate-700">
                {i.name}
              </ColorChip>
              <div>
                <BudgetMiniBar pct={i.pct ?? (i.budget_cents ? (i.spent_cents / i.budget_cents) * 100 : 0)} status={i.status} />
                <p className="text-[11px] text-slate-400 tabular-nums">
                  {formatBRL(i.spent_cents)} de {formatBRL(i.budget_cents)}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

interface KpiProps {
  label: string;
  value: number;
  icon: React.ReactNode;
  tone: string;
  hint?: string;
  onClick?: () => void;
}

const Kpi: React.FC<KpiProps> = ({ label, value, icon, tone, hint, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!onClick}
    className="card p-4 text-left hover:shadow-card-hover transition-shadow disabled:cursor-default disabled:hover:shadow-card"
  >
    <div className="flex items-center justify-between">
      <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</span>
      <span className={clsx('p-1.5 rounded-lg', tone)}>{icon}</span>
    </div>
    <p className="text-3xl font-semibold text-slate-900 mt-2">{value}</p>
    {hint && <p className="text-small mt-1">{hint}</p>}
  </button>
);

const ListCard: React.FC<{
  title: string;
  icon: React.ReactNode;
  items: Activity[];
  total: number;
  empty: string;
  onOpen: (a: Activity) => void;
  moreLink?: string;
}> = ({ title, icon, items, total, empty, onOpen, moreLink }) => (
  <div className="card p-4 flex flex-col min-h-[220px]">
    <div className="flex items-center justify-between mb-2">
      <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
        {icon}
        {title}
        <span className="badge-neutral">{total}</span>
      </h3>
      {moreLink && total > items.length && (
        <Link to={moreLink} className="text-xs text-emerald-700 hover:underline">
          Ver todas
        </Link>
      )}
    </div>
    {items.length === 0 ? (
      <p className="text-sm text-slate-400 py-6 text-center flex-1 flex items-center justify-center">{empty}</p>
    ) : (
      <div className="space-y-0.5 max-h-80 overflow-y-auto custom-scrollbar -mx-1">
        {items.map((a) => (
          <ActivityRow key={a.id} activity={a} onClick={() => onOpen(a)} />
        ))}
      </div>
    )}
  </div>
);

/**
 * "Hoje": o que vence hoje, o que atrasou, o que está sem prazo (pendente de priorização) e os
 * próximos dias — só do usuário logado —, os vencimentos de documentos (CAC e comuns), as
 * próximas faturas de cartão (Fase 4), o orçamento do mês (Fase 5, só quando há orçamento) e o
 * resumo financeiro (Fase 6, só quando há conta ativa) e as contas a agendar no banco (Fase 7).
 */
export const DashboardView: React.FC = () => {
  const { user } = useAuth();
  const firstName = user?.name?.split(' ')[0] ?? '';
  const { openActivity, openNewActivity, version } = useActivityModal();
  const { features } = useProfile();
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await dashboardService.getSummary());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao carregar o painel');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, version]);

  const open = (a: Activity) => openActivity(a.id);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-h2">Olá{firstName ? `, ${firstName}` : ''}!</h1>
          {data && <p className="text-slate-500 mt-1">Hoje é {formatDateBR(data.today)}</p>}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" icon={<RefreshCw />} onClick={load} disabled={loading}>
            Atualizar
          </Button>
          <PermissionGuard permission="agenda.activities.write">
            <Button variant="primary" size="sm" icon={<Plus />} onClick={() => openNewActivity()}>
              Nova atividade
            </Button>
          </PermissionGuard>
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
      )}

      {loading && !data ? (
        <div className="flex items-center justify-center py-24 text-slate-400">
          <Loader2 size={20} className="animate-spin mr-2" /> Carregando…
        </div>
      ) : data ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-7 gap-4">
            <Kpi
              label="Hoje"
              value={data.due_today}
              icon={<AlarmClock size={16} className="text-amber-600" />}
              tone="bg-amber-50"
              hint="vencem hoje"
            />
            <Kpi
              label="Atrasadas"
              value={data.overdue}
              icon={<AlertTriangle size={16} className="text-red-600" />}
              tone="bg-red-50"
              onClick={() => navigate('/activities?overdue=1')}
            />
            <Kpi
              label="Sem prazo"
              value={data.no_due_date}
              icon={<Inbox size={16} className="text-violet-600" />}
              tone="bg-violet-50"
              hint="pendentes de priorização"
              onClick={() => navigate('/activities?no_due_date=1')}
            />
            <Kpi
              label="Críticas"
              value={data.critical}
              icon={<Flame size={16} className="text-rose-600" />}
              tone="bg-rose-50"
              onClick={() => navigate('/activities?priority=critical')}
            />
            <Kpi
              label="Próximos 7 dias"
              value={data.upcoming}
              icon={<CalendarClock size={16} className="text-sky-600" />}
              tone="bg-sky-50"
            />
            <Kpi
              label="Concluídas hoje"
              value={data.completed_today}
              icon={<CheckCircle2 size={16} className="text-emerald-600" />}
              tone="bg-emerald-50"
            />
            <Kpi
              label="Alertas"
              value={data.unread_notifications}
              icon={<Bell size={16} className="text-slate-600" />}
              tone="bg-slate-100"
              hint="não lidos"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ListCard
              title="Vencem hoje"
              icon={<AlarmClock size={16} className="text-amber-600" />}
              items={data.due_today_list}
              total={data.due_today}
              empty="Nada vence hoje."
              onOpen={open}
            />
            <ListCard
              title="Atrasadas"
              icon={<AlertTriangle size={16} className="text-red-600" />}
              items={data.overdue_list}
              total={data.overdue}
              empty="Nenhuma atividade atrasada. 🎉"
              onOpen={open}
              moreLink="/activities?overdue=1"
            />
            <ListCard
              title="Pendentes de priorização"
              icon={<Inbox size={16} className="text-violet-600" />}
              items={data.no_due_date_list}
              total={data.no_due_date}
              empty="Todas as atividades têm prazo."
              onOpen={open}
              moreLink="/activities?no_due_date=1"
            />
            <ListCard
              title="Próximos dias"
              icon={<CalendarClock size={16} className="text-sky-600" />}
              items={data.upcoming_list}
              total={data.upcoming}
              empty="Nada previsto para os próximos dias."
              onOpen={open}
            />
          </div>

          {/* Vencimentos: documentos CAC (só com o módulo liberado) e documentos com validade */}
          <div className="card p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                <FileWarning size={16} className="text-amber-600" />
                Vencimentos
                <span className="badge-neutral">{data.expiring_count}</span>
              </h3>
              <Link to={features.cac ? '/cac/expiring' : '/documents?expiring=1'} className="text-xs text-emerald-700 hover:underline">
                {features.cac ? 'Ver todos' : 'Ver documentos'}
              </Link>
            </div>
            {data.expiring_documents.length === 0 ? (
              <p className="text-sm text-slate-400 py-4 text-center">Nenhum documento vencido ou vencendo em breve.</p>
            ) : (
              <div className="divide-y divide-slate-100 -mx-1">
                {data.expiring_documents.map((i) => (
                  <ExpiringRow key={`${i.kind}-${i.id}`} item={i} compact />
                ))}
              </div>
            )}
          </div>

          {/* Contas a agendar (Fase 7): só quando o backend manda `scheduling` */}
          {data.scheduling && (
            <PermissionGuard permission="financas.read">
              <SchedulingWidget scheduling={data.scheduling} onChanged={load} />
            </PermissionGuard>
          )}

          {/* Finanças (Fase 6): só quando o usuário tem conta ativa (o backend omite `finance`) */}
          {data.finance && (
            <PermissionGuard permission="financas.read">
              <FinanceWidget finance={data.finance} today={data.today} />
            </PermissionGuard>
          )}

          {/* Orçamento do mês (só quando há alguma categoria orçada) */}
          {data.budget && data.budget.total_budget_cents > 0 && (
            <PermissionGuard permission="financas.read">
              <BudgetWidget budget={data.budget} />
            </PermissionGuard>
          )}

          {/* Próximas faturas de cartão: não pagas vencendo em 30 dias ou vencidas (até 5) */}
          <PermissionGuard permission="financas.read">
            <div className="card p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                  <CreditCard size={16} className="text-violet-600" />
                  Próximas faturas
                  <span className="badge-neutral">{data.upcoming_invoices.length}</span>
                </h3>
                <Link to="/finance/cards" className="text-xs text-emerald-700 hover:underline">
                  Ver cartões
                </Link>
              </div>
              {data.upcoming_invoices.length === 0 ? (
                <p className="text-sm text-slate-400 py-4 text-center">Nenhuma fatura vencendo nos próximos 30 dias.</p>
              ) : (
                <div className="divide-y divide-slate-100 -mx-1">
                  {data.upcoming_invoices.map((inv) => (
                    <InvoiceRow key={inv.id} invoice={inv} today={data.today} />
                  ))}
                </div>
              )}
            </div>
          </PermissionGuard>

          {data.boards.length > 0 && (
            <div>
              <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2 mb-3">
                <CalendarRange size={16} className="text-slate-500" /> Projetos ativos
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {data.boards.map((b) => {
                  const cc = colorClasses(b.color);
                  return (
                    <Link
                      key={b.id}
                      to={`/kanban/${b.id}`}
                      className={clsx('card-hover p-4 border-l-4 block', cc.borderLeft)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-900 truncate">{b.name}</p>
                          <p className="text-small">{BOARD_TYPE_LABELS[b.board_type]}</p>
                        </div>
                        <span className={clsx('w-3 h-3 rounded-full flex-shrink-0 mt-1', cc.swatch)} />
                      </div>
                      {(b.start_date || b.end_date) && (
                        <p className="text-xs text-slate-500 mt-2">
                          {b.start_date ? formatDateBR(b.start_date) : '…'} a {b.end_date ? formatDateBR(b.end_date) : '…'}
                        </p>
                      )}
                      <div className="flex gap-4 mt-3 text-sm">
                        <span className="text-slate-600">
                          <strong className="text-slate-900">{b.open_count}</strong> abertas
                        </span>
                        <span className={clsx(b.overdue_count > 0 ? 'text-red-600' : 'text-slate-400')}>
                          <strong>{b.overdue_count}</strong> atrasadas
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
};
